import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, Table, CopyButton } from '../../../components';

export const StructuredDataSection = ({ id, data }) => {
  const [schemaOrgExpanded, setSchemaOrgExpanded] = useState(false);
  const [openGraphExpanded, setOpenGraphExpanded] = useState(false);
  const [twitterCardExpanded, setTwitterCardExpanded] = useState(false);

  // Default criteria for when data is not available
  const defaultCriteria = {
    status: 'neutral',
    iconType: 'info',
    importance: { level: 0, text: '' },
    solvability: { level: 0, text: '' },
    isEmpty: true,
    message: 'Data is unavailable.',
    data: null,
  };

  // Extract section data with defaults
  const schemaOrg = data?.schemaOrg || defaultCriteria;
  const openGraph = data?.openGraph || defaultCriteria;
  const twitterCard = data?.twitterCard || defaultCriteria;
  const microformats = data?.microformats || defaultCriteria;

  // Build Schema.org table data
  const schemaOrgTableData = schemaOrg?.data?.schemaTable?.length > 0
    ? [['Type', 'Count'], ...schemaOrg.data.schemaTable.map((item) => [item.type, String(item.count)])]
    : [];

  // Build Open Graph properties table data
  const openGraphTableData = openGraph?.data?.propertiesTable?.length > 0
    ? [['Tag', 'Value'], ...openGraph.data.propertiesTable.map((item) => [item.tag, item.value])]
    : [];

  // Build Open Graph issues table data
  const openGraphIssuesTableData = openGraph?.data?.issuesTable?.length > 0
    ? [['Property', 'Issue'], ...openGraph.data.issuesTable.map((item) => [item.property, item.type])]
    : [];

  // Build Twitter Card properties table data
  const twitterCardTableData = twitterCard?.data?.propertiesTable?.length > 0
    ? [['Tag', 'Value'], ...twitterCard.data.propertiesTable.map((item) => [item.tag, item.value])]
    : [];

  // Build Twitter Card errors table data
  const twitterCardErrorsTableData = twitterCard?.data?.errorsTable?.length > 0
    ? [['Property', 'Issue'], ...twitterCard.data.errorsTable.map((item) => [item.property, item.type])]
    : [];

  // Build Microformats table data
  const microformatsTableData = microformats?.data?.microformatsTable?.length > 0
    ? [['Type', 'Count'], ...microformats.data.microformatsTable.map((item) => [item.type, String(item.count)])]
    : [];

  return (
    <ResultsItem 
      id={id}
      title="Structured Data" 
      tooltip="Structured data, also called schema markup, tells search engines about your content. Search engines use structured data to learn more about your content and get definitive answers about its purpose and focus. Is your site using structured data correctly, though? Our SEO checker has your answer!"
      errorCount={data?.errorCount || 0}
      toImproveCount={data?.toImproveCount || 0}
    >
      {/* Schema.org */}
      <ResultSubItem
        title="Schema.org"
        tooltip="Your schema.org is the structured data on your website, which provides search engines with additional and definitive information about your site, business, and content."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={schemaOrg?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!schemaOrg?.isEmpty && schemaOrg?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={schemaOrgTableData}
                  className="table-schema-org"
                  id="schema-org-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#schema-org-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{schemaOrg?.message || "We didn't detect any schema.org items on your webpage"}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={schemaOrgExpanded}
            onClick={() => setSchemaOrgExpanded(!schemaOrgExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={schemaOrgExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Schema.org Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Generate structured data for existing pages with Google's Structured Data Markup Helper tool</li>
                <li>Format structured data as JSON-LD versus microdata</li>
                <li>Check structured data for errors with Google's Structured Testing Tool</li>
              </ul>
            </IconContent>
          }
          sideContent={
            schemaOrg?.importance?.level > 0 || schemaOrg?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {schemaOrg?.importance?.level > 0 && (
                  <PriorityIndicator text={schemaOrg?.importance?.text || ''} level={schemaOrg?.importance?.level || 0} />
                )}
                {schemaOrg?.solvability?.level > 0 && (
                  <PriorityIndicator text={schemaOrg?.solvability?.text || ''} level={schemaOrg?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/seo/learn/structured-data/" target="_blank" rel="noopener noreferrer">
                    Structured Data 101: What Is Structured Data & How Do I Use It?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/seo/how-to-improve-seo-with-structured-data/" target="_blank" rel="noopener noreferrer">
                    How to Improve SEO With Structured Data in 7 Steps
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/seo/what-is-schema-markup/" target="_blank" rel="noopener noreferrer">
                    What Is Schema Markup and How Do I Implement It for SEO?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      {/* Open Graph Protocol */}
      <ResultSubItem
        title="Open Graph Protocol"
        tooltip="Facebook developed the Open Graph protocol to enable the integration of any web page with its social media platform. Other social media platforms have also adopted the protocol, allowing you to control how your web pages are presented when shared across social media."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={openGraph?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!openGraph?.isEmpty && openGraph?.data ? (
              <div className="open-graph-content">
                {/* Properties table */}
                <div className="table-content-wrapper" style={{ marginBottom: openGraphIssuesTableData.length > 0 ? '16px' : '0' }}>
                <Table
                    data={openGraphTableData}
                  className="table-open-graph"
                  id="open-graph-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#open-graph-table" />
                  </div>
                </div>

                {/* Issues table */}
                {openGraphIssuesTableData.length > 0 && (
                  <div className="table-content-wrapper">
                    <Table
                      data={openGraphIssuesTableData}
                      className="table-open-graph-issues"
                      id="open-graph-issues-table"
                    />
                    <div className="copy-button-wrapper">
                      <CopyButton targetSelector="#open-graph-issues-table" />
                </div>
              </div>
            )}
              </div>
            ) : (
              <p className="text-primary">{openGraph?.message || 'No Open Graph Tags were found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={openGraphExpanded}
            onClick={() => setOpenGraphExpanded(!openGraphExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={openGraphExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Open Graph Protocol Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Implement essential OG tags such as og:title, og:description, og:image, and og:url within your website's &lt;head&gt; section</li>
                <li>Use the Facebook sharing debugger tool to preview how your pages will look when shared</li>
                <li>Leverage SEO plugins like Yoast SEO to simplify the addition and management of Open Graph Tags</li>
              </ul>
            </IconContent>
          }
          sideContent={
            openGraph?.importance?.level > 0 || openGraph?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {openGraph?.importance?.level > 0 && (
                  <PriorityIndicator text={openGraph?.importance?.text || ''} level={openGraph?.importance?.level || 0} />
                )}
                {openGraph?.solvability?.level > 0 && (
                  <PriorityIndicator text={openGraph?.solvability?.text || ''} level={openGraph?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="https://www.webfx.com/blog/social-media/open-graph-meta-tags/" target="_blank" rel="noopener noreferrer">
                    Open Graph Meta Tags: Your Guide to OG Meta Tags
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.webfx.com/blog/seo/seo-cheat-sheet/" target="_blank" rel="noopener noreferrer">
                    The Technical SEO Cheat Sheet for Webmasters and Developers
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      {/* Twitter Card */}
      <ResultSubItem
        title="Twitter Card"
        tooltip="Twitter Cards are designed to give users a rich media experience whenever a tweet contains links to a site's content. Twitter has various card types that can show content previews, play videos and increase traffic to your website."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={twitterCard?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!twitterCard?.isEmpty && twitterCard?.data ? (
              <div className="twitter-card-content">
                {/* Properties table */}
                <div className="table-content-wrapper" style={{ marginBottom: twitterCardErrorsTableData.length > 0 ? '16px' : '0' }}>
                <Table
                    data={twitterCardTableData}
                  className="table-twitter-card"
                  id="twitter-card-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#twitter-card-table" />
                  </div>
                </div>

                {/* Errors table */}
                {twitterCardErrorsTableData.length > 0 && (
                  <div className="table-content-wrapper">
                    <Table
                      data={twitterCardErrorsTableData}
                      className="table-twitter-card-errors"
                      id="twitter-card-errors-table"
                    />
                    <div className="copy-button-wrapper">
                      <CopyButton targetSelector="#twitter-card-errors-table" />
                </div>
              </div>
            )}
              </div>
            ) : (
              <p className="text-primary">{twitterCard?.message || 'No Twitter Card was found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={twitterCardExpanded}
            onClick={() => setTwitterCardExpanded(!twitterCardExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={twitterCardExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Twitter Card Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Add Twitter card meta tags such as twitter:card, twitter:title, twitter:description, and twitter:image in your page's &lt;head&gt;</li>
                <li>Use Twitter's tool to preview and troubleshoot how your cards will display</li>
                <li>Make sure you're choosing the right card type (e.g., summary, summary_large_image, player)</li>
              </ul>
            </IconContent>
          }
          sideContent={
            twitterCard?.importance?.level > 0 || twitterCard?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {twitterCard?.importance?.level > 0 && (
                  <PriorityIndicator text={twitterCard?.importance?.text || ''} level={twitterCard?.importance?.level || 0} />
                )}
                {twitterCard?.solvability?.level > 0 && (
                  <PriorityIndicator text={twitterCard?.solvability?.text || ''} level={twitterCard?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="https://www.webfx.com/social-media/learn/twitter-ad-specs/" target="_blank" rel="noopener noreferrer">
                    A Guide to Twitter Ad Specs in 2025
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.webfx.com/blog/social-media/twitter-ads-for-beginners/" target="_blank" rel="noopener noreferrer">
                    X (Twitter) Advertising 101: A Beginner's Guide to Twitter Ads
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      {/* Microformats - No Tips accordion */}
      <ResultSubItem
        title="Microformats"
        tooltip="Designed for humans first and machines second, microformats use code (HTML/XHTML tags) originally intended for other purposes to add context to the content on a webpage. This helps machines (like Google!) to understand certain information (contact information, geographic coordinates, calendar events, etc.) intended for humans."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={microformats?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!microformats?.isEmpty && microformats?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={microformatsTableData}
                  className="table-microformats"
                  id="microformats-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#microformats-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{microformats?.message || 'No Microformats were found.'}</p>
            )}
          </IconContent>
        }
      />
    </ResultsItem>
  );
};
